import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { adsAllowedForPath, adsConfig } from '@/config/ads';

type SlotName = keyof typeof adsConfig.slots;

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

export function AdSlot({
  slot,
  label = 'Advertisement',
  /** When true, ignore route allowlist (used inside guide pages already gated). */
  force = false,
}: {
  slot: SlotName;
  label?: string;
  force?: boolean;
}) {
  const ref = useRef<HTMLModElement>(null);
  const initialized = useRef(false);
  const { pathname } = useLocation();
  const routeOk = force || adsAllowedForPath(pathname);

  useEffect(() => {
    if (!adsConfig.enabled || !routeOk || initialized.current) return;
    const slotId = adsConfig.slots[slot];
    if (!slotId || !adsConfig.clientId) return;
    initialized.current = true;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      // Ad blockers / missing script — ignore
    }
  }, [slot, routeOk]);

  if (!routeOk) return null;

  if (!adsConfig.enabled) {
    if (!adsConfig.showPlaceholdersInDev || !import.meta.env.DEV) return null;
    return (
      <aside className="ad-slot" data-label={label} aria-label={label}>
        <span>Ad placeholder ({slot}) — disabled until publisher IDs are configured</span>
      </aside>
    );
  }

  const slotId = adsConfig.slots[slot];
  if (!slotId) {
    // Client script in index.html is enough for AdSense verification; hide empty units.
    return null;
  }

  return (
    <aside className="ad-slot" data-label={label} aria-label={label}>
      <ins
        ref={ref}
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={adsConfig.clientId}
        data-ad-slot={slotId}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </aside>
  );
}
